import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardBody, CardTitle, Row, Col } from 'reactstrap';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Pagination from './Pagination';

const MyReferrals = () => {
  const [stats, setStats] = useState(null);
  const [referredUsers, setReferredUsers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  // Helper function to extract user ID from JWT token as fallback
  const getUserIdFromToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      const decoded = JSON.parse(jsonPayload);
      return decoded.id;
    } catch (e) {
      console.error('[MyReferrals] Error decoding token:', e);
      return null;
    }
  };

  useEffect(() => {
    // Get ambassador ID from localStorage
    const userStr = localStorage.getItem('user');
    const token = localStorage.getItem('accessToken');
    console.log('[MyReferrals] userStr from localStorage:', userStr);
    console.log('[MyReferrals] token present:', !!token);
    
    let ambassadorId = null;
    
    // Try to get ID from user object first
    if (userStr && userStr !== 'undefined' && userStr !== 'null' && userStr.trim() !== '') {
      try {
        const user = JSON.parse(userStr);
        console.log('[MyReferrals] Parsed user object:', user);
        
        if (user && user.id) {
          ambassadorId = user.id;
          console.log('[MyReferrals] Using ambassador ID from user object:', ambassadorId);
        }
      } catch (e) {
        console.error('[MyReferrals] Error parsing user:', e, 'Raw string:', userStr);
      }
    }
    
    // Fallback: Try to get ID from JWT token
    if (!ambassadorId && token) {
      ambassadorId = getUserIdFromToken(token);
      console.log('[MyReferrals] Using ambassador ID from token:', ambassadorId);
    }
    
    if (ambassadorId) {
      fetchStats(ambassadorId);
      fetchReferredUsers(ambassadorId);
    } else {
      console.error('[MyReferrals] Unable to get ambassador ID from user object or token');
      setError('Unable to identify ambassador. Please log in again.');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    const token = localStorage.getItem('accessToken');
    
    let ambassadorId = null;
    
    // Try to get ID from user object first
    if (userStr && userStr !== 'undefined' && userStr !== 'null' && userStr.trim() !== '') {
      try {
        const user = JSON.parse(userStr);
        if (user && user.id) {
          ambassadorId = user.id;
        }
      } catch (e) {
        console.error('[MyReferrals] Error parsing user in pagination effect:', e);
      }
    }
    
    // Fallback: Try to get ID from JWT token
    if (!ambassadorId && token) {
      ambassadorId = getUserIdFromToken(token);
    }
    
    if (ambassadorId) {
      fetchReferredUsers(ambassadorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchStats = async (ambassadorId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE_URL}/admin/campus/ambassador-overview/${ambassadorId}/stats`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching ambassador stats:', err);
      setError(err.response?.data?.error || 'Failed to load ambassador statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferredUsers = async (ambassadorId) => {
    try {
      setUsersLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE_URL}/admin/campus/ambassador-overview/${ambassadorId}/referred-users`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { page: currentPage, limit: 50 }
        }
      );
      setReferredUsers(response.data.users || []);
      setPagination(response.data.pagination || null);
    } catch (err) {
      console.error('Error fetching referred users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="alert alert-info" role="alert">
        No data available.
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <h1 className="mb-2 fw-bold" style={{ fontSize: '1.875rem', color: '#1e293b' }}>
          My Referrals
        </h1>
        <p className="text-muted mb-0 small">
          {stats.ambassador.email}
          {stats.ambassador.universityName && ` • ${stats.ambassador.universityName}`}
        </p>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <CardBody style={{ minHeight: '120px', display: 'flex', flexDirection: 'column' }}>
              <CardTitle tag="h6" className="text-muted mb-2">Total Users Onboarded</CardTitle>
              <h2 className="fw-bold mb-0" style={{ fontSize: '2.5rem', color: '#1e293b' }}>
                {stats.totalUsersOnboarded}
              </h2>
            </CardBody>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <CardBody style={{ minHeight: '120px', display: 'flex', flexDirection: 'column' }}>
              <CardTitle tag="h6" className="text-muted mb-2">Amount Earned</CardTitle>
              <h2 className="fw-bold mb-0" style={{ fontSize: '2.5rem', color: '#16a34a' }}>
                ₹{stats.amountPaid?.toLocaleString('en-IN') || 0}
              </h2>
              <span 
                className="small fw-medium mt-1"
                style={{ 
                  color: stats.amountPending > 0 ? '#dc3545' : '#64748b',
                  fontSize: '0.75rem',
                  lineHeight: '1'
                }}
              >
                Pending: ₹{stats.amountPending?.toLocaleString('en-IN') || 0}
              </span>
            </CardBody>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <CardBody style={{ minHeight: '120px', display: 'flex', flexDirection: 'column' }}>
              <CardTitle tag="h6" className="text-muted mb-2">Verification Pending</CardTitle>
              <h2 className="fw-bold mb-0" style={{ fontSize: '2.5rem', color: '#EA580C' }}>
                {stats.verificationPending || 0}
              </h2>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row className="mb-4">
        <Col md={6} className="mb-4">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h5" className="mb-3">Last 7 Days</CardTitle>
              {stats.last7Days && stats.last7Days.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={stats.last7Days}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#4C1D95"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="Users Onboarded"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted text-center py-4">No data available for the last 7 days</p>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col md={6} className="mb-4">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h5" className="mb-3">Last 4 Weeks</CardTitle>
              {stats.last4Weeks && stats.last4Weeks.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={stats.last4Weeks}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="week"
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#4C1D95"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="Users Onboarded"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted text-center py-4">No data available for the last 4 weeks</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Referred Users Table */}
      <Card className="shadow-sm border-0">
        <CardBody>
          <CardTitle tag="h5" className="mb-3">Referred Users</CardTitle>
          {usersLoading ? (
            <div className="d-flex justify-content-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : referredUsers.length === 0 ? (
            <p className="text-muted text-center py-4">No referred users yet.</p>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Verified</th>
                      <th>Date of Joining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{user.serialNumber}</td>
                        <td>{user.name}</td>
                        <td>
                          <span className={`badge ${user.verified ? 'bg-success' : 'bg-warning'}`}>
                            {user.verified ? 'Verified' : 'Pending'}
                          </span>
                        </td>
                        <td>{user.dateOfJoiningFormatted}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pagination && (
                <Pagination pagination={pagination} onPageChange={handlePageChange} />
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default MyReferrals;

